import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Bot, Sparkles, Send, ArrowLeft, Bug } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import MainLayout from '../Layout/MainLayout';

const BankAgentChat = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const bottomRef = useRef(null);
  const transactionInitializedRef = useRef(false);

  // Get transaction data from URL
  const transactionData = searchParams.get('transaction');
  const parentWorkflowId = searchParams.get('parentWorkflow');
  const parentRunId = searchParams.get('parentRun');
  const urlChildRunId = searchParams.get('childRun');

  // State
  const [transaction, setTransaction] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [threadId, setThreadId] = useState(null);
  const [childWorkflowId, setChildWorkflowId] = useState(null);
  const [childRunId, setChildRunId] = useState(urlChildRunId || null);
  const [assistantId, setAssistantId] = useState(null);
  const [error, setError] = useState(null);

  // Manual modification state
  const [selectedEntityId, setSelectedEntityId] = useState('');
  const [enableReallocation, setEnableReallocation] = useState(false);
  const [allocationAmounts, setAllocationAmounts] = useState({});
  const [isUpdatingEntity, setIsUpdatingEntity] = useState(false);
  const [isUpdatingAllocation, setIsUpdatingAllocation] = useState(false);
  const [isTransferringToJE, setIsTransferringToJE] = useState(false);
  const [transactionDataSent, setTransactionDataSent] = useState(false);

  // All entities for dropdown (fetched from NetSuite when not matched)
  const [allEntities, setAllEntities] = useState([]);
  const [isLoadingEntities, setIsLoadingEntities] = useState(false);
  const [entitySearchTerm, setEntitySearchTerm] = useState('');


  // JE Agent specific state
  const [jeLines, setJeLines] = useState([]);
  const [accountGroups, setAccountGroups] = useState({});
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false);
  const [selectedJeLines, setSelectedJeLines] = useState({});
  const [jeLineUpdates, setJeLineUpdates] = useState({}); // {line_index: new_account_id}
  const [jeMassUpdateEnabled, setJeMassUpdateEnabled] = useState(false);
  const [jeMassUpdateAccountId, setJeMassUpdateAccountId] = useState('');
  const [isApplyingJeUpdates, setIsApplyingJeUpdates] = useState(false);
  const [accountSearchTerm, setAccountSearchTerm] = useState('');
  const [netsuiteAccountId, setNetsuiteAccountId] = useState('');
  const [jeLineMemos, setJeLineMemos] = useState({}); // {line_index: memo_value}
  const [jeLineDepartments, setJeLineDepartments] = useState({}); // {line_index: department_id}
  const [jeLineClasses, setJeLineClasses] = useState({}); // {line_index: class_id}
  const [jeLineLocations, setJeLineLocations] = useState({}); // {line_index: location_id}
  const [departmentList, setDepartmentList] = useState([]); // [{id, name}, ...]
  const [classList, setClassList] = useState([]); // [{id, name}, ...]
  const [locationList, setLocationList] = useState([]); // [{id, name}, ...]
  const [isLoadingClassesLocations, setIsLoadingClassesLocations] = useState(false);

  // Build NetSuite URL based on account_id
  const getNetSuiteUrl = (recordType, recordId) => {
    // Replace underscores with hyphens for NetSuite URL format
    const formattedAccountId = netsuiteAccountId ? netsuiteAccountId.replace(/_/g, '-') : '';
    const baseUrl = formattedAccountId
      ? `https://${formattedAccountId}.app.netsuite.com`
      : 'https://system.netsuite.com';

    const recordPaths = {
      'custpymt': 'app/accounting/transactions/custpymt.nl',
      'vendpymt': 'app/accounting/transactions/vendpymt.nl',
      'journal': 'app/accounting/transactions/journal.nl'
    };

    const path = recordPaths[recordType] || `app/accounting/transactions/${recordType}.nl`;
    return `${baseUrl}/${path}?id=${recordId}`;
  };

  // Format message content - hide JE Lines JSON and show a clean message
  const formatMessageContent = (content) => {
    if (!content) return content;

    // Check if message contains "JE Lines with selected accounts:" and JSON
    if (content.includes('JE Lines with selected accounts:')) {
      // Extract just the first line/action before the JSON
      const parts = content.split('\n\nJE Lines with selected accounts:');
      if (parts.length > 0 && parts[0]) {
        // Return just the action message like "create journal entry"
        return parts[0].trim() + '\n\n(JE details applied)';
      }
    }

    return content;
  };

  // Parse transaction data on mount - only runs ONCE
  // Uses ref to prevent re-running when URL updates trigger re-renders
  useEffect(() => {
    // Skip if already initialized (prevents overwriting on URL changes)
    if (transactionInitializedRef.current) {
      console.log('🔴 [URL useEffect] Already initialized, skipping');
      return;
    }

    if (transactionData) {
      try {
        const parsed = JSON.parse(decodeURIComponent(transactionData));

        const normalizedTransaction = {
          Amount: parsed.amount || parsed.Amount || '',
          bank: parsed.bank || '',
          customername: parsed.customername || '',
          vendorname: parsed.vendorname || '',
          description: parsed.description || '',
          invoicelist: parsed.invoicelist || [],
          billlist: parsed.billlist || [],
          invoicenumber: parsed.invoicenumber || '',
          ns_pay_doc: parsed.ns_pay_doc || [],
          ns_payment_id: parsed.ns_payment_id || [],
          ns_billpayment_id: parsed.ns_billpayment_id || [],
          ns_billpay_doc: parsed.ns_billpay_doc || [],
          ns_je_id: parsed.ns_je_id || parsed.ns_JE_id || [],
          ns_je_doc: parsed.ns_je_doc || parsed.ns_JE_doc || [],
          nscustomerinternalid: parsed.nscustomerinternalid || '',
          nscustomername: parsed.nscustomername || '',
          nsvendorinternalid: parsed.nsvendorinternalid || '',
          nsvendorname: parsed.nsvendorname || '',
          nsinvoiceamount: parsed.nsinvoiceamount || [],
          nsinvoicedocnum: parsed.nsinvoicedocnum || [],
          nsinvoiceinternalid: parsed.nsinvoiceinternalid || [],
          nsbillamount: parsed.nsbillamount || [],
          nsbilldocnum: parsed.nsbilldocnum || [],
          nsbillinternalid: parsed.nsbillinternalid || [],
          similarcustomer: parsed.similarcustomer || [],
          similarvendor: parsed.similarvendor || [],
          trandate: parsed.date || parsed.trandate || '',
          transactiontype: parsed.transactiontype || 'credit',
          agent_type: parsed.agent_type || 'bank_agent',
          transaction_index: parsed.transaction_index || 0,
          _original: parsed
        };

        // Mark as initialized BEFORE setting state
        transactionInitializedRef.current = true;
        console.log('🔴 [URL useEffect] Setting initial transaction from URL');

        setTransaction(normalizedTransaction);

        const welcomeMessage = {
          id: 'welcome',
          type: 'assistant',
          content: `Hello! I'm here to help you process this ${getTransactionTypeName(parsed.agent_type)} transaction.\n\n**Transaction Details:**\n- **Amount:** ${normalizedTransaction.Amount}\n- **Date:** ${normalizedTransaction.trandate}\n- **Description:** ${normalizedTransaction.description}\n- **${parsed.agent_type === 'bill_agent' ? 'Vendor' : 'Customer'}:** ${normalizedTransaction.customername || normalizedTransaction.vendorname || 'Not identified'}\n\nPlease send a message to start the payment processing workflow.`,
          timestamp: new Date().toISOString()
        };

        setMessages([welcomeMessage]);
      } catch (err) {
        console.error('Error parsing transaction data:', err);
        setError('Invalid transaction data');
      }
    } else {
      setError('No transaction data provided');
    }
  }, [transactionData]);

  // Auto scroll to bottom
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Load existing run info and chat history when childRunId is available (for page reload persistence)
  useEffect(() => {
    const loadExistingSession = async () => {
      if (!childRunId || !token) return;

      try {
        // First load run info to restore workflow state
        const runInfoResponse = await fetch(`/api/bank/v2/workflows/run-info/${childRunId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (runInfoResponse.ok) {
          const runInfo = await runInfoResponse.json();
          if (runInfo.success) {
            // Restore workflow state
            if (runInfo.workflow_id) setChildWorkflowId(runInfo.workflow_id);
            if (runInfo.assistant_id) setAssistantId(runInfo.assistant_id);
            if (runInfo.thread_id) setThreadId(runInfo.thread_id);
            console.log('Restored workflow state:', {
              workflowId: runInfo.workflow_id,
              assistantId: runInfo.assistant_id,
              threadId: runInfo.thread_id
            });
          }
        }

        // Load saved transaction data (payment links, invoice info, etc.)
        const txResponse = await fetch(`/api/bank/v2/workflows/get-transaction/${childRunId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (txResponse.ok) {
          const txData = await txResponse.json();
          if (txData.success && txData.transaction && Object.keys(txData.transaction).length > 0) {
            console.log('🟢 [API useEffect] txData.transaction vendor:', txData.transaction.nsvendorinternalid, txData.transaction.nsvendorname);
            setTransaction(prev => {
              const merged = { ...prev, ...txData.transaction };
              console.log('🟢 [API useEffect] Merged transaction vendor:', merged.nsvendorinternalid, merged.nsvendorname);
              return merged;
            });
            console.log('Restored transaction data with payment info');

            // Set NetSuite account ID for URL generation
            if (txData.netsuite_account_id) {
              setNetsuiteAccountId(txData.netsuite_account_id);
              console.log('NetSuite account ID:', txData.netsuite_account_id);
            }

            // Restore manual allocation amounts if they exist AND no payment has been created yet
            // If payment already exists, don't restore old allocations to avoid duplicate payments
            const hasPayment = (txData.transaction.ns_payment_id?.length > 0) ||
                               (txData.transaction.ns_billpayment_id?.length > 0) ||
                               (txData.transaction.ns_je_id?.length > 0) ||
                               (txData.transaction.ns_JE_id?.length > 0);

            if (!hasPayment && txData.transaction.manual_allocations && txData.transaction.manual_allocations.length > 0) {
              const restoredAllocations = {};
              txData.transaction.manual_allocations.forEach(alloc => {
                // Support both invoice_id (new) and doc_number (legacy) field names
                const docKey = alloc.invoice_id || alloc.doc_number;
                if (docKey) {
                  restoredAllocations[docKey] = alloc.amount;
                }
              });
              setAllocationAmounts(restoredAllocations);
              setEnableReallocation(true);
              console.log('Restored manual allocations:', restoredAllocations);
            } else if (hasPayment) {
              console.log('Payment already exists, not restoring manual allocations');
            }
          }
        }

        // Then load chat history
        const chatResponse = await fetch(`/api/bank/v2/workflows/chat-history/${childRunId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (chatResponse.ok) {
          const data = await chatResponse.json();
          if (data.success && data.messages && data.messages.length > 0) {
            // Helper function to parse stored message content
            const parseMessageContent = (content, type) => {
              if (!content) return '';

              // For user messages, extract text before "Transaction data:"
              if (type === 'user') {
                const transactionIndex = content.indexOf('\n\nTransaction data:');
                if (transactionIndex > -1) {
                  return content.substring(0, transactionIndex).trim();
                }
                return content;
              }

              // For assistant messages, try to parse JSON and extract meaningful text
              if (type === 'assistant') {
                try {
                  // Check if it starts with { - likely JSON
                  const trimmed = content.trim();
                  if (trimmed.startsWith('{')) {
                    const parsed = JSON.parse(trimmed);

                    // First priority: user_message field (this is the AI's readable response)
                    if (parsed.user_message) {
                      return parsed.user_message;
                    }

                    // Second priority: message field
                    if (parsed.message) {
                      return parsed.message;
                    }

                    // Fallback: generate summary from bankdata
                    if (parsed.bankdata) {
                      const bankData = Array.isArray(parsed.bankdata) ? parsed.bankdata[0] : parsed.bankdata;
                      if (bankData) {
                        const amount = bankData.Amount ? `₹${Number(bankData.Amount).toLocaleString('en-IN')}` : '';
                        const customer = bankData.customername || bankData.vendorname || '';
                        return `Transaction data for ${customer}. Amount: ${amount}`;
                      }
                    }
                  }
                } catch (e) {
                  // Not valid JSON, return as-is
                }
              }

              return content;
            };

            // Filter to only user and assistant messages
            const chatMessages = data.messages
              .filter(msg => msg.type === 'user' || msg.type === 'assistant')
              .map(msg => ({
                id: msg.id,
                type: msg.type,
                content: parseMessageContent(msg.content, msg.type),
                timestamp: msg.timestamp
              }))
              .filter(msg => msg.content && msg.content.trim() !== '');

            if (chatMessages.length > 0) {
              // Keep welcome message and add loaded messages
              setMessages(prev => {
                const welcomeMsg = prev.find(m => m.id === 'welcome');
                return welcomeMsg ? [welcomeMsg, ...chatMessages] : chatMessages;
              });
              console.log(`Loaded ${chatMessages.length} chat messages from history`);
            }
          }
        }
      } catch (err) {
        console.error('Error loading existing session:', err);
      }
    };

    loadExistingSession();
  }, [childRunId, token]);

  // Check for existing workflow on page load (when coming from TransactionsModal without childRun in URL)
  useEffect(() => {
    const checkExistingWorkflow = async () => {
      // Only run if we have parentRunId but no childRunId
      if (!parentRunId || childRunId || !token || !transaction) return;

      console.log('Checking for existing workflow on page load...');

      try {
        const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://platform.sankalvax.ai';
        const response = await fetch(`${API_BASE_URL}/api/bank/v2/workflows/create-child`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            parent_workflow_id: parentWorkflowId,
            parent_run_id: parentRunId,
            transaction_data: transaction,
            workflow_type: 'transaction_approval'
          })
        });

        if (response.ok) {
          const data = await response.json();
          console.log('Existing workflow check result:', data);

          if (data.child_run_id) {
            // Found or created workflow - set states
            setChildWorkflowId(data.workflow_id);
            setChildRunId(data.child_run_id);
            if (data.assistant_id) setAssistantId(data.assistant_id);
            if (data.thread_id) {
              setThreadId(data.thread_id);
            }

            // For existing workflows, update transaction with latest data (may have manual entity mappings)
            if (data.existing && data.updated_transaction) {
              console.log('🔄 Updating transaction with latest data from existing workflow:', data.updated_transaction);
              const mergedTransaction = {
                ...transaction,
                ...data.updated_transaction,
                transaction_index: transaction.transaction_index // Preserve original index
              };
              setTransaction(mergedTransaction);
            }

            // Store pre-fetched invoices/bills if available (only for new workflows)
            // And immediately trigger AI to show allocation message
            if (!data.existing) {
              let hasPrefetchedData = false;
              let prefetchedInvoices = null;
              let prefetchedBills = null;

              if (data.invoicelist && data.invoicelist.length > 0) {
                prefetchedInvoices = data.invoicelist;
                console.log(`✅ Pre-fetched ${data.invoicelist.length} invoices`);
                hasPrefetchedData = true;
              }
              if (data.billlist && data.billlist.length > 0) {
                prefetchedBills = data.billlist;
                console.log(`✅ Pre-fetched ${data.billlist.length} bills`);
                hasPrefetchedData = true;
              }

              // Trigger AI to show allocation message only if entity is mapped
              // Skip for JE agent - it doesn't use invoice/bill allocation
              // Skip for unmapped vendor/customer transactions - user needs to select entity first
              const isMapped = transaction.agent_type === 'bill_agent'
                ? !!transaction.nsvendorinternalid
                : transaction.agent_type === 'bank_agent'
                ? !!transaction.nscustomerinternalid
                : true; // JE agent doesn't need mapping check

              if (transaction && transaction.agent_type !== 'je_agent' && isMapped) {
                // Update transaction with pre-fetched data and allocation arrays
                const updatedTx = {
                  ...transaction,
                  invoicelist: prefetchedInvoices || transaction.invoicelist || [],
                  billlist: prefetchedBills || transaction.billlist || [],
                  // Include allocation arrays from backend
                  nsinvoicedocnum: data.nsinvoicedocnum || [],
                  nsinvoiceamount: data.nsinvoiceamount || [],
                  nsinvoiceinternalid: data.nsinvoiceinternalid || [],
                  nsbilldocnum: data.nsbilldocnum || [],
                  nsbillamount: data.nsbillamount || [],
                  nsbillinternalid: data.nsbillinternalid || []
                };
                setTransaction(updatedTx);
                console.log('📊 Updated transaction with allocation:', updatedTx.nsinvoicedocnum || updatedTx.nsbilldocnum);

                // Immediately call AI with pre-fetched data
                (async () => {
                  try {
                    const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://platform.sankalvax.ai';
                    const currentAssistantId = data.assistant_id;
                    const currentChildRunId = data.child_run_id;

                    // Create thread
                    let currentThreadId = data.thread_id;
                    if (!currentThreadId) {
                      console.log('🧵 Creating thread for auto-trigger...');
                      const threadResponse = await fetch(`${API_BASE_URL}/api/bank/v2/workflows/openai/thread/create`, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                          'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify({
                          assistant_id: currentAssistantId,
                          run_id: currentChildRunId
                        })
                      });

                      if (threadResponse.ok) {
                        const threadData = await threadResponse.json();
                        currentThreadId = threadData.thread_id;
                        setThreadId(currentThreadId);
                      } else {
                        console.error('Failed to create thread for auto-trigger');
                        return;
                      }
                    }

                    // Prepare message
                    const isBillAgent = updatedTx.agent_type === 'bill_agent';
                    const entityName = isBillAgent
                      ? (updatedTx.nsvendorname || updatedTx.vendorname || 'Vendor')
                      : (updatedTx.nscustomername || updatedTx.customername || 'Customer');
                    const dataType = isBillAgent ? 'bills' : 'invoices';

                    const autoMessage = `Show me the open ${dataType} for ${entityName} and suggest allocation for this payment.`;

                    // Show user message, replace welcome
                    setMessages([{
                      id: `auto-${Date.now()}`,
                      type: 'user',
                      content: autoMessage,
                      timestamp: new Date().toISOString()
                    }]);
                    setIsLoading(true);
                    setTransactionDataSent(true);

                    // Prepare transaction schema
                    const txSchema = {
                      Amount: updatedTx.Amount,
                      bank: updatedTx.bank,
                      customername: updatedTx.customername,
                      vendorname: updatedTx.vendorname,
                      description: updatedTx.description,
                      invoicelist: updatedTx.invoicelist,
                      billlist: updatedTx.billlist,
                      nscustomerinternalid: updatedTx.nscustomerinternalid,
                      nscustomername: updatedTx.nscustomername,
                      nsvendorinternalid: updatedTx.nsvendorinternalid,
                      nsvendorname: updatedTx.nsvendorname,
                      trandate: updatedTx.trandate,
                      transactiontype: updatedTx.transactiontype
                    };

                    const formattedMessage = `${autoMessage}\n\nTransaction data:\n${JSON.stringify([txSchema], null, 2)}`;

                    // Send to AI - include child_run_id in transaction_context for message persistence
                    const sendResponse = await fetch(`${API_BASE_URL}/api/bank/v2/workflows/openai/message/send`, {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                      },
                      body: JSON.stringify({
                        thread_id: currentThreadId,
                        assistant_id: currentAssistantId,
                        message: formattedMessage,
                        transaction_context: {
                          ...updatedTx,
                          child_run_id: currentChildRunId
                        }
                      })
                    });

                    if (sendResponse.ok) {
                      const respData = await sendResponse.json();
                      let aiContent = respData.response || respData.message;

                      // Extract user_message from JSON response if present
                      if (aiContent) {
                        try {
                          const parsed = typeof aiContent === 'string' ? JSON.parse(aiContent) : aiContent;
                          if (parsed.user_message) {
                            aiContent = parsed.user_message;
                          }
                        } catch (e) {
                          // Not JSON, use as-is
                        }
                      }

                      if (aiContent) {
                        setMessages(prev => [...prev, {
                          id: `ai-${Date.now()}`,
                          type: 'assistant',
                          content: aiContent,
                          timestamp: new Date().toISOString()
                        }]);
                      }
                    }
                  } catch (err) {
                    console.error('Auto-trigger AI error:', err);
                  } finally {
                    setIsLoading(false);
                  }
                })();
              }
            }

            // Update URL with childRunId for persistence
            const newParams = new URLSearchParams(searchParams);
            newParams.set('childRun', data.child_run_id);
            navigate(`${window.location.pathname}?${newParams.toString()}`, { replace: true });

            console.log(data.existing ? 'Restored existing workflow:' : 'Created new workflow:', data.child_run_id);
          }
        }
      } catch (err) {
        console.error('Error checking for existing workflow:', err);
      }
    };

    checkExistingWorkflow();
  }, [parentRunId, parentWorkflowId, transaction, token, childRunId]);

  // Fetch all entities when NOT matched (for dropdown selection)
  useEffect(() => {
    const fetchAllEntities = async () => {
      if (!transaction || !parentRunId || !token) return;

      // Determine if matched based on agent type
      // bank_agent (AR) = customers, bill_agent (AP) = vendors
      const isBillAgent = transaction.agent_type === 'bill_agent';
      const matched = isBillAgent
        ? !!transaction.nsvendorinternalid
        : !!transaction.nscustomerinternalid;

      // Don't fetch if already matched
      if (matched) {
        setAllEntities([]);
        return;
      }

      setIsLoadingEntities(true);

      try {
        const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://platform.sankalvax.ai';

        // IMPORTANT: Fetch correct entity type based on agent
        // bill_agent (AP) = vendors, bank_agent (AR) = customers
        const entityEndpoint = isBillAgent ? 'vendors' : 'customers';

        console.log(`🔍 Fetching ALL ${entityEndpoint} for ${transaction.agent_type}`);

        const entityResponse = await fetch(`${API_BASE_URL}/api/bank/v2/workflows/data/${entityEndpoint}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (entityResponse.ok) {
          const entityData = await entityResponse.json();
          const entities = (entityData.data || []).map(e => ({
            value: e.value,
            label: e.label
          }));
          setAllEntities(entities);
          console.log(`✅ Fetched ${entities.length} ${entityEndpoint} for selection`);
        } else {
          console.error(`Failed to fetch ${entityEndpoint}:`, entityResponse.status);
        }
      } catch (err) {
        console.error('Error fetching entities:', err);
      } finally {
        setIsLoadingEntities(false);
      }
    };

    fetchAllEntities();
  }, [transaction, parentRunId, token]);

  // Fetch accounts, classes, and locations for JE Agent
  useEffect(() => {
    const fetchAccountsForJE = async () => {
      if (!transaction || transaction.agent_type !== 'je_agent' || !token) return;

      setIsLoadingAccounts(true);

      try {
        const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://platform.sankalvax.ai';
        const response = await fetch(`${API_BASE_URL}/api/bank/v2/workflows/data/accounts`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          setAccountGroups(data.groups || {});
          console.log(`📊 Fetched ${data.count || 0} accounts in ${Object.keys(data.groups || {}).length} groups`);
        } else {
          console.error('Failed to fetch accounts:', response.status);
        }
      } catch (err) {
        console.error('Error fetching accounts:', err);
      } finally {
        setIsLoadingAccounts(false);
      }
    };

    // Fetch departments, classes and locations for JE Agent
    const fetchDepartmentsClassesLocations = async () => {
      if (!transaction || transaction.agent_type !== 'je_agent' || !token) return;

      setIsLoadingClassesLocations(true);

      try {
        const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://platform.sankalvax.ai';

        // Fetch departments, classes and locations in parallel
        const [departmentsRes, classesRes, locationsRes] = await Promise.all([
          fetch(`${API_BASE_URL}/api/bank/v2/workflows/data/departments`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }),
          fetch(`${API_BASE_URL}/api/bank/v2/workflows/data/classes`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }),
          fetch(`${API_BASE_URL}/api/bank/v2/workflows/data/locations`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          })
        ]);

        if (departmentsRes.ok) {
          const departmentsData = await departmentsRes.json();
          setDepartmentList(departmentsData.items || []);
          console.log(`📊 Fetched ${departmentsData.count || 0} departments`);
        } else {
          console.error('Failed to fetch departments:', departmentsRes.status);
        }

        if (classesRes.ok) {
          const classesData = await classesRes.json();
          setClassList(classesData.items || []);
          console.log(`📊 Fetched ${classesData.count || 0} classes`);
        } else {
          console.error('Failed to fetch classes:', classesRes.status);
        }

        if (locationsRes.ok) {
          const locationsData = await locationsRes.json();
          setLocationList(locationsData.items || []);
          console.log(`📊 Fetched ${locationsData.count || 0} locations`);
        } else {
          console.error('Failed to fetch locations:', locationsRes.status);
        }
      } catch (err) {
        console.error('Error fetching departments/classes/locations:', err);
      } finally {
        setIsLoadingClassesLocations(false);
      }
    };

    fetchAccountsForJE();
    fetchDepartmentsClassesLocations();

    // Initialize JE lines from transaction data - but only if jeLines is empty
    // This prevents resetting user's selections when transaction updates from AI response
    if (transaction && transaction.agent_type === 'je_agent') {
      setJeLines(prevJeLines => {
        // If we already have jeLines with data, don't reset them
        if (prevJeLines.length > 0) {
          console.log(`📊 JE Lines already exist (${prevJeLines.length} lines), preserving user selections`);
          return prevJeLines;
        }

        // Check if we have explicit je_lines or lines array
        let lines = transaction.je_lines || transaction.lines || [];

        // If no lines array, create a single line from the transaction itself
        if (lines.length === 0) {
          lines = [{
            srno: 1,
            description: transaction.description || '',
            memo: transaction.description || '',
            transactiontype: transaction.transactiontype || (parseFloat(transaction.Amount) >= 0 ? 'credit' : 'debit'),
            type: parseFloat(transaction.Amount) >= 0 ? 'credit' : 'debit',
            Amount: transaction.Amount,
            amount: transaction.Amount,
            debit: parseFloat(transaction.Amount) < 0 ? Math.abs(transaction.Amount) : 0,
            credit: parseFloat(transaction.Amount) >= 0 ? transaction.Amount : 0,
            nsaccountid: transaction.nsaccountid || '',
            nsaccountname: transaction.nsaccountname || '',
            account_name: transaction.nsaccountname || '',
            current_account: transaction.nsaccountname || '',
            trandate: transaction.trandate,
            bank: transaction.bank,
            // Original transaction reference
            customername: transaction.customername,
            vendorname: transaction.vendorname,
            original_agent_type: transaction.original_agent_type
          }];
        }

        console.log(`📊 JE Lines initialized: ${lines.length} line(s)`);
        return lines;
      });
    }
  }, [transaction, token]);

  const getTransactionTypeName = (agentType) => {
    switch (agentType) {
      case 'bank_agent': return 'Customer Payment (AR)';
      case 'bill_agent': return 'Vendor Payment (AP)';
      case 'je_agent': return 'Journal Entry';
      default: return agentType;
    }
  };

  const formatAmount = (n) => {
    const num = Number(n || 0);
    return num.toLocaleString('en-IN');
  };

  // Check if entity is matched
  const isMatched = () => {
    if (!transaction) return false;
    if (transaction.agent_type === 'bill_agent') {
      return !!transaction.nsvendorinternalid;
    }
    return !!transaction.nscustomerinternalid;
  };

  const getMatchedName = () => {
    if (!transaction) return '';
    if (transaction.agent_type === 'bill_agent') {
      return transaction.nsvendorname || '';
    }
    return transaction.nscustomername || '';
  };

  const getPredictedName = () => {
    if (!transaction) return 'N/A';
    if (transaction.agent_type === 'bill_agent') {
      return transaction.vendorname || 'N/A';
    }
    return transaction.customername || 'N/A';
  };

  // Get similar entities for dropdown
  const getSimilarEntities = () => {
    if (!transaction) return [];
    if (transaction.agent_type === 'bank_agent') {
      return (transaction.similarcustomer || []).map(entity => ({
        value: typeof entity === 'object' ? entity.internalid : entity,
        label: typeof entity === 'object' ? entity.name : entity
      }));
    } else if (transaction.agent_type === 'bill_agent') {
      return (transaction.similarvendor || []).map(entity => ({
        value: typeof entity === 'object' ? entity.internalid : entity,
        label: typeof entity === 'object' ? entity.name : entity
      }));
    }
    return [];
  };

  // Get open invoices/bills
  const getOpenDocs = () => {
    if (!transaction) return [];
    if (transaction.agent_type === 'bank_agent') {
      return (transaction.invoicelist || []).map((inv, idx) => ({
        id: inv.internalid || transaction.nsinvoiceinternalid?.[idx] || `inv-${idx}`,
        number: inv.invoicenumber || inv.docnum || `Invoice ${idx + 1}`,
        amount: inv.invoice_total || inv.openbalance || inv.amount || 0,
        duedate: inv.duedate || ''
      }));
    } else if (transaction.agent_type === 'bill_agent') {
      return (transaction.billlist || []).map((bill, idx) => ({
        id: bill.internalid || transaction.nsbillinternalid?.[idx] || `bill-${idx}`,
        number: bill.billnumber || bill.docnum || `Bill ${idx + 1}`,
        amount: bill.bill_total || bill.openbalance || bill.amount || 0,
        duedate: bill.duedate || ''
      }));
    }
    return [];
  };

  // Get suggested allocation (prefers manual_allocations if available)
  const getSuggestedAllocation = () => {
    if (!transaction) return [];

    // If user has set manual allocations, use those instead
    if (transaction.manual_allocations && transaction.manual_allocations.length > 0) {
      return transaction.manual_allocations.map(alloc => ({
        // Support both invoice_id (new) and doc_number (legacy) field names
        number: alloc.invoice_id || alloc.doc_number || 'Unknown',
        amount: alloc.amount,
        isManual: true
      }));
    }

    if (transaction.agent_type === 'bank_agent') {
      // First try nsinvoicedocnum/nsinvoiceamount (AI suggested allocation)
      const docNums = transaction.nsinvoicedocnum || [];
      const amounts = transaction.nsinvoiceamount || [];
      if (docNums.length > 0) {
        return docNums.map((num, idx) => ({
          number: num,
          amount: amounts[idx] || 0
        }));
      }
      // Fallback to invoicelist if no AI allocation
      const invoiceList = transaction.invoicelist || [];
      if (invoiceList.length > 0) {
        return invoiceList.map((inv) => ({
          number: inv.invoicenumber || inv.docnum || 'Invoice',
          amount: inv.invoice_total || inv.openbalance || inv.amount || 0
        }));
      }
      return [];
    } else if (transaction.agent_type === 'bill_agent') {
      // First try nsbilldocnum/nsbillamount (AI suggested allocation)
      const docNums = transaction.nsbilldocnum || [];
      const amounts = transaction.nsbillamount || [];
      if (docNums.length > 0) {
        return docNums.map((num, idx) => ({
          number: num,
          amount: amounts[idx] || 0
        }));
      }
      // Fallback to billlist if no AI allocation
      const billList = transaction.billlist || [];
      if (billList.length > 0) {
        return billList.map((bill) => ({
          number: bill.billnumber || bill.docnum || 'Bill',
          amount: bill.bill_total || bill.openbalance || bill.amount || 0
        }));
      }
      return [];
    }
    return [];
  };

  // Get payment links
  const getPaymentLinks = () => {
    if (!transaction) return [];
    if (transaction.agent_type === 'bank_agent') {
      const ids = transaction.ns_payment_id || [];
      const docs = transaction.ns_pay_doc || [];
      return ids.map((id, idx) => ({
        id,
        label: docs[idx] || `Payment ${id}`,
        url: getNetSuiteUrl('custpymt', id),
        type: 'customer'
      }));
    } else if (transaction.agent_type === 'bill_agent') {
      const ids = transaction.ns_billpayment_id || [];
      const docs = transaction.ns_billpay_doc || [];
      return ids.map((id, idx) => ({
        id,
        label: docs[idx] || `Payment ${id}`,
        url: getNetSuiteUrl('vendpymt', id),
        type: 'vendor'
      }));
    } else if (transaction.agent_type === 'je_agent') {
      // Handle both ns_je_id and ns_JE_id (backend uses capital JE)
      // Use length check since empty array is truthy
      const ids = (transaction.ns_JE_id?.length > 0 ? transaction.ns_JE_id : null) || (transaction.ns_je_id?.length > 0 ? transaction.ns_je_id : null) || [];
      const docs = (transaction.ns_JE_doc?.length > 0 ? transaction.ns_JE_doc : null) || (transaction.ns_je_doc?.length > 0 ? transaction.ns_je_doc : null) || [];
      console.log('🔍 JE Payment Links Debug:', {
        agent_type: transaction.agent_type,
        ns_JE_id: transaction.ns_JE_id,
        ns_je_id: transaction.ns_je_id,
        ns_JE_doc: transaction.ns_JE_doc,
        ns_je_doc: transaction.ns_je_doc,
        ids,
        docs
      });
      return ids.map((id, idx) => ({
        id,
        label: docs[idx] || `JE ${id}`,
        url: getNetSuiteUrl('journal', id),
        type: 'je'
      }));
    }
    return [];
  };

  // Create child workflow
  const createChildWorkflow = async () => {
    if (!parentWorkflowId || !parentRunId) {
      console.error('Missing parent workflow information');
      return null;
    }

    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://platform.sankalvax.ai';
      const response = await fetch(`${API_BASE_URL}/api/bank/v2/workflows/create-child`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          parent_workflow_id: parentWorkflowId,
          parent_run_id: parentRunId,
          transaction_data: transaction,
          workflow_type: 'transaction_approval'
        })
      });

      if (!response.ok) throw new Error('Failed to create child workflow');

      const data = await response.json();
      setChildWorkflowId(data.workflow_id);
      setChildRunId(data.child_run_id);
      setAssistantId(data.assistant_id);

      // If existing workflow returned thread_id, use it
      if (data.thread_id) {
        setThreadId(data.thread_id);
        console.log('Using existing thread:', data.thread_id);
      }

      // Update URL with childRunId for persistence on page reload
      const newParams = new URLSearchParams(searchParams);
      newParams.set('childRun', data.child_run_id);
      navigate(`${window.location.pathname}?${newParams.toString()}`, { replace: true });

      return { workflowId: data.workflow_id, runId: data.child_run_id, assistantId: data.assistant_id, threadId: data.thread_id };
    } catch (err) {
      console.error('Error creating child workflow:', err);
      setError('Failed to create child workflow');
      return null;
    }
  };

  // Create OpenAI thread
  const createThread = async (currentAssistantId = null, currentChildRunId = null) => {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://platform.sankalvax.ai';
      const effectiveAssistantId = currentAssistantId || assistantId;
      const effectiveChildRunId = currentChildRunId || childRunId;

      if (!effectiveAssistantId) throw new Error('No assistant ID available');

      const response = await fetch(`${API_BASE_URL}/api/bank/v2/workflows/openai/thread/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          assistant_id: effectiveAssistantId,
          metadata: {
            transaction_type: transaction.agent_type,
            transaction_index: transaction.transaction_index,
            parent_workflow_id: parentWorkflowId,
            parent_run_id: parentRunId,
            child_workflow_id: childWorkflowId,
            child_run_id: effectiveChildRunId
          }
        })
      });

      if (!response.ok) throw new Error('Failed to create thread');

      const data = await response.json();
      setThreadId(data.thread_id);
      return data.thread_id;
    } catch (err) {
      console.error('Error creating thread:', err);
      setError('Failed to initialize chat');
      return null;
    }
  };

  // Send message to OpenAI
  const sendMessageToAssistant = async (message, currentThreadId, currentChildRunId = null, currentAssistantId = null) => {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://platform.sankalvax.ai';
      const effectiveAssistantId = currentAssistantId || assistantId;
      const effectiveChildRunId = currentChildRunId || childRunId;

      if (!effectiveAssistantId) throw new Error('No assistant ID available');

      // Check if transaction data has been sent to the assistant yet
      const needsTransactionData = !transactionDataSent;
      let formattedMessage = message;

      if (needsTransactionData) {
        const transactionSchema = {
          Amount: transaction.Amount,
          bank: transaction.bank,
          customername: transaction.customername,
          vendorname: transaction.vendorname,
          description: transaction.description,
          invoicelist: transaction.invoicelist,
          billlist: transaction.billlist,
          invoicenumber: transaction.invoicenumber,
          ns_pay_doc: transaction.ns_pay_doc,
          ns_payment_id: transaction.ns_payment_id,
          nscustomerinternalid: transaction.nscustomerinternalid,
          nscustomername: transaction.nscustomername,
          nsvendorinternalid: transaction.nsvendorinternalid,
          nsvendorname: transaction.nsvendorname,
          nsinvoiceamount: transaction.nsinvoiceamount,
          nsinvoicedocnum: transaction.nsinvoicedocnum,
          nsinvoiceinternalid: transaction.nsinvoiceinternalid,
          nsbillamount: transaction.nsbillamount,
          nsbilldocnum: transaction.nsbilldocnum,
          nsbillinternalid: transaction.nsbillinternalid,
          trandate: transaction.trandate,
          transactiontype: transaction.transactiontype,
          // Include selected account if available
          nsaccountid: transaction.nsaccountid || transaction.new_account_id,
          nsaccountname: transaction.nsaccountname || transaction.account_name
        };
        formattedMessage = `${message}\n\nTransaction data:\n${JSON.stringify([transactionSchema], null, 2)}`;
        setTransactionDataSent(true);
      }

      // For JE Agent: Include jeLines with selected accounts, memos, department, class, and location in the message
      if (transaction?.agent_type === 'je_agent' && jeLines.length > 0) {
        // Merge jeLineUpdates, jeLineMemos, jeLineDepartments, jeLineClasses, jeLineLocations into jeLines for the message
        const jeLinesWithAccounts = jeLines.map((line, idx) => {
          const pendingAccountId = jeLineUpdates[idx];
          const lineMemo = jeLineMemos[idx];
          const lineDepartment = jeLineDepartments[idx];
          const lineClass = jeLineClasses[idx];
          const lineLocation = jeLineLocations[idx];
          let updatedLine = { ...line };

          if (pendingAccountId) {
            let accountName = pendingAccountId;
            Object.values(accountGroups).forEach(accounts => {
              const found = accounts.find(a => a.id === pendingAccountId);
              if (found) accountName = found.name;
            });
            updatedLine.nsaccountid = pendingAccountId;
            updatedLine.nsaccountname = accountName;
          }

          // Only include memo if user has entered a value
          if (lineMemo && lineMemo.trim()) {
            updatedLine.memo = lineMemo.trim();
          }

          // Only include department if user has selected a value
          if (lineDepartment) {
            updatedLine.department_id = lineDepartment;
            const deptObj = departmentList.find(d => d.id === lineDepartment);
            if (deptObj) updatedLine.department_name = deptObj.name;
          }

          // Only include class if user has selected a value
          if (lineClass) {
            updatedLine.class_id = lineClass;
            const classObj = classList.find(c => c.id === lineClass);
            if (classObj) updatedLine.class_name = classObj.name;
          }

          // Only include location if user has selected a value
          if (lineLocation) {
            updatedLine.location_id = lineLocation;
            const locObj = locationList.find(l => l.id === lineLocation);
            if (locObj) updatedLine.location_name = locObj.name;
          }

          return updatedLine;
        });

        formattedMessage += `\n\nJE Lines with selected accounts:\n${JSON.stringify(jeLinesWithAccounts, null, 2)}`;
      }

      const response = await fetch(`${API_BASE_URL}/api/bank/v2/workflows/openai/message/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          thread_id: currentThreadId,
          assistant_id: effectiveAssistantId,
          message: formattedMessage,
          transaction_context: {
            ...transaction,
            child_run_id: effectiveChildRunId
          }
        })
      });

      if (!response.ok) throw new Error('Failed to send message');
      return await response.json();
    } catch (err) {
      console.error('Error sending message:', err);
      throw err;
    }
  };

  // Handle send message
  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    setIsLoading(true);
    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: inputMessage,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    const messageToSend = inputMessage;
    setInputMessage('');

    try {
      let currentChildWorkflowId = childWorkflowId;
      let currentChildRunId = childRunId;
      let currentAssistantId = assistantId;
      let currentThreadId = threadId;

      if (!currentChildWorkflowId) {
        const childWorkflowResult = await createChildWorkflow();
        if (!childWorkflowResult) throw new Error('Failed to create child workflow');
        currentChildWorkflowId = childWorkflowResult.workflowId;
        currentChildRunId = childWorkflowResult.runId;
        currentAssistantId = childWorkflowResult.assistantId;
        // Use existing thread_id if returned from existing workflow
        if (childWorkflowResult.threadId) {
          currentThreadId = childWorkflowResult.threadId;
        }
      }

      if (!currentThreadId) {
        currentThreadId = await createThread(currentAssistantId, currentChildRunId);
        if (!currentThreadId) throw new Error('Failed to create thread');
      }

      const response = await sendMessageToAssistant(messageToSend, currentThreadId, currentChildRunId, currentAssistantId);

      let assistantContent = response.response || response.message;
      let newTransaction = { ...transaction };

      // Extract payment info from payment_results (returned by backend) - do this first
      const paymentResults = response.payment_results || [];
      for (const paymentResult of paymentResults) {
        if (paymentResult.ns_billpayment_id && paymentResult.status === 'Success') {
          // Add vendor payment IDs to transaction
          newTransaction = {
            ...newTransaction,
            ns_billpayment_id: [...(newTransaction.ns_billpayment_id || []), paymentResult.ns_billpayment_id],
            ns_billpay_doc: [...(newTransaction.ns_billpay_doc || []), paymentResult.ns_billpay_doc]
          };
          console.log('💰 Vendor payment created:', paymentResult.ns_billpay_doc);
        }
        if (paymentResult.ns_payment_id && paymentResult.status === 'Success') {
          // Add customer payment IDs to transaction
          newTransaction = {
            ...newTransaction,
            ns_payment_id: [...(newTransaction.ns_payment_id || []), paymentResult.ns_payment_id],
            ns_pay_doc: [...(newTransaction.ns_pay_doc || []), paymentResult.ns_pay_doc]
          };
          console.log('💰 Customer payment created:', paymentResult.ns_pay_doc);
        }
        // Handle JE results - check both ns_je_id and ns_JE_id (backend uses capital JE)
        const jeId = paymentResult.ns_je_id || paymentResult.ns_JE_id;
        const jeDoc = paymentResult.ns_je_doc || paymentResult.ns_JE_doc;
        if (jeId && paymentResult.status === 'Success') {
          // Add journal entry IDs to transaction
          newTransaction = {
            ...newTransaction,
            ns_je_id: [...(newTransaction.ns_je_id || []), jeId],
            ns_je_doc: [...(newTransaction.ns_je_doc || []), jeDoc]
          };
          console.log('📒 Journal Entry created:', jeDoc);
        }
      }

      try {
        const parsedResponse = JSON.parse(assistantContent);
        if (parsedResponse.user_message) {
          assistantContent = parsedResponse.user_message;
        }
        // Update transaction data if returned
        if (parsedResponse.bankdata) {
          const updatedTx = parsedResponse.bankdata[0] || parsedResponse.bankdata;
          // Preserve manual_allocations (used by all agent types)
          const preservedAllocations = newTransaction.manual_allocations;
          newTransaction = { ...newTransaction, ...updatedTx };
          // Restore manual_allocations if AI didn't include them
          if (preservedAllocations && !updatedTx.manual_allocations) {
            newTransaction.manual_allocations = preservedAllocations;
          }
          // For JE agent only: preserve account and line selections
          if (transaction?.agent_type === 'je_agent') {
            if (transaction.nsaccountid && !updatedTx.nsaccountid) {
              newTransaction.nsaccountid = transaction.nsaccountid;
              newTransaction.nsaccountname = transaction.nsaccountname;
              newTransaction.account_name = transaction.account_name;
            }
            if (transaction.je_lines && !updatedTx.je_lines) {
              newTransaction.je_lines = transaction.je_lines;
            }
            if (transaction.lines && !updatedTx.lines) {
              newTransaction.lines = transaction.lines;
            }
          }
        }
      } catch (e) {
        // Not JSON, use original
      }

      // Save updated transaction if we have payment results or bankdata
      if (paymentResults.length > 0 || newTransaction !== transaction) {
        setTransaction(newTransaction);

        // Save transaction to database for persistence
        if (childRunId) {
          fetch(`/api/bank/v2/workflows/save-transaction/${childRunId}`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ transaction: newTransaction })
          }).catch(err => console.warn('Failed to save transaction:', err));
        }
      }

      const assistantMessage = {
        id: Date.now() + 1,
        type: 'assistant',
        content: assistantContent,
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (err) {
      console.error('Error in handleSendMessage:', err);
      const errorMessage = {
        id: Date.now() + 1,
        type: 'error',
        content: `Error: ${err.message}`,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle entity update
  const handleEntityUpdate = async () => {
    if (!selectedEntityId || !parentRunId) return;

    // Check similarEntities first, then allEntities
    const similarEntitiesList = getSimilarEntities();
    let selectedOption = similarEntitiesList.find(e => e.value === selectedEntityId);

    // If not found in similar, check allEntities
    if (!selectedOption) {
      selectedOption = allEntities.find(e => e.value === selectedEntityId);
    }

    if (!selectedOption) {
      console.error('Selected entity not found in any list');
      return;
    }

    // IMPORTANT: Correct entity type based on agent
    // bank_agent (AR) = customer, bill_agent (AP) = vendor
    const entityType = transaction.agent_type === 'bill_agent' ? 'vendor' : 'customer';
    const noun = transaction.agent_type === 'bill_agent' ? 'Vendor' : 'Customer';

    setIsUpdatingEntity(true);

    // Add user message to chat
    const userMsg = `Please update ${noun} ${selectedOption.label} (nsinternalid="${selectedEntityId}") for ${getPredictedName()}.`;
    setMessages(prev => [...prev, { id: Date.now(), type: 'user', content: userMsg, timestamp: new Date().toISOString() }]);

    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://platform.sankalvax.ai';
      const response = await fetch(`${API_BASE_URL}/api/bank/v2/workflows/update-entity`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          parent_run_id: parentRunId,
          child_run_id: childRunId,
          transaction_index: transaction.transaction_index,
          transaction_description: transaction.description,  // For fast matching
          entity_type: entityType,
          entity_id: selectedEntityId,
          entity_name: selectedOption.label
        })
      });

      const data = await response.json();

      if (data.success) {
        // Build updated transaction with new entity info AND pre-fetched allocation data
        const updatedTransaction = {
          ...transaction,
          ...(entityType === 'customer'
            ? {
                nscustomerinternalid: selectedEntityId,
                nscustomername: selectedOption.label,
                // Include pre-fetched invoice allocation from backend
                invoicelist: data.invoicelist || transaction.invoicelist || [],
                nsinvoicedocnum: data.nsinvoicedocnum || transaction.nsinvoicedocnum || [],
                nsinvoiceamount: data.nsinvoiceamount || transaction.nsinvoiceamount || [],
                nsinvoiceinternalid: data.nsinvoiceinternalid || transaction.nsinvoiceinternalid || []
              }
            : {
                nsvendorinternalid: selectedEntityId,
                nsvendorname: selectedOption.label,
                // Include pre-fetched bill allocation from backend
                billlist: data.billlist || transaction.billlist || [],
                nsbilldocnum: data.nsbilldocnum || transaction.nsbilldocnum || [],
                nsbillamount: data.nsbillamount || transaction.nsbillamount || [],
                nsbillinternalid: data.nsbillinternalid || transaction.nsbillinternalid || []
              }
          )
        };
        setTransaction(updatedTransaction);
        console.log('📊 [Entity Update] Updated transaction with allocation:', {
          invoices: updatedTransaction.nsinvoicedocnum,
          bills: updatedTransaction.nsbilldocnum
        });

        // Persist the updated transaction to database so it survives page refresh
        if (childRunId) {
          console.log('💾 [Entity Update] Saving updated transaction to database, childRunId:', childRunId);
          fetch(`${API_BASE_URL}/api/bank/v2/workflows/save-transaction/${childRunId}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ transaction: updatedTransaction })
          })
            .then(res => res.json())
            .then(saveResult => {
              console.log('💾 [Entity Update] Save result:', saveResult);
            })
            .catch(err => {
              console.warn('⚠️ [Entity Update] Failed to persist transaction:', err);
            });
        }

        // Show success message for entity update
        setMessages(prev => [...prev, {
          id: Date.now(),
          type: 'assistant',
          content: `✅ ${noun} updated to ${selectedOption.label}`,
          timestamp: new Date().toISOString()
        }]);

        // Now trigger AI to fetch invoices/bills (same as Approve flow)
        // Need threadId and assistantId - create if not available
        let currentThreadId = threadId;
        let currentAssistantId = assistantId;

        // If no thread/assistant yet, we need to create them first
        if (!currentThreadId || !currentAssistantId) {
          console.log('🔧 [Entity Update] No thread/assistant - creating...');
          try {
            // First ensure we have assistant_id from child workflow
            if (!currentAssistantId && childRunId) {
              const runInfoRes = await fetch(`${API_BASE_URL}/api/bank/v2/workflows/run-info/${childRunId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
              });
              if (runInfoRes.ok) {
                const runInfo = await runInfoRes.json();
                currentAssistantId = runInfo.assistant_id;
                if (currentAssistantId) setAssistantId(currentAssistantId);
              }
            }

            // Create thread if needed (use same metadata as createThread function)
            if (!currentThreadId && currentAssistantId) {
              const threadRes = await fetch(`${API_BASE_URL}/api/bank/v2/workflows/openai/thread/create`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                  assistant_id: currentAssistantId,
                  metadata: {
                    transaction_type: transaction.agent_type,
                    transaction_index: transaction.transaction_index,
                    parent_workflow_id: parentWorkflowId,
                    parent_run_id: parentRunId,
                    child_workflow_id: childWorkflowId,
                    child_run_id: childRunId
                  }
                })
              });
              if (threadRes.ok) {
                const threadData = await threadRes.json();
                currentThreadId = threadData.thread_id;
                if (currentThreadId) setThreadId(currentThreadId);
              }
            }
          } catch (setupErr) {
            console.error('Failed to setup thread/assistant:', setupErr);
          }
        }

        if (currentThreadId && currentAssistantId) {
          const dataType = entityType === 'vendor' ? 'bills' : 'invoices';
          const autoMessage = `Show me the open ${dataType} for ${selectedOption.label} and suggest allocation for this payment.`;

          // Add user message to chat
          setMessages(prev => [...prev, {
            id: `auto-${Date.now()}`,
            type: 'user',
            content: autoMessage,
            timestamp: new Date().toISOString()
          }]);

          setIsLoading(true);

          // Prepare transaction schema for AI
          const txSchema = {
            Amount: updatedTransaction.Amount,
            bank: updatedTransaction.bank,
            customername: updatedTransaction.customername,
            vendorname: updatedTransaction.vendorname,
            description: updatedTransaction.description,
            nscustomerinternalid: updatedTransaction.nscustomerinternalid,
            nscustomername: updatedTransaction.nscustomername,
            nsvendorinternalid: updatedTransaction.nsvendorinternalid,
            nsvendorname: updatedTransaction.nsvendorname,
            trandate: updatedTransaction.trandate,
            transactiontype: updatedTransaction.transactiontype
          };

          const formattedMessage = `${autoMessage}\n\nTransaction data:\n${JSON.stringify([txSchema], null, 2)}`;

          try {
            // Send to AI assistant
            const sendResponse = await fetch(`${API_BASE_URL}/api/bank/v2/workflows/openai/message/send`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({
                thread_id: currentThreadId,
                assistant_id: currentAssistantId,
                message: formattedMessage,
                transaction_context: {
                  ...updatedTransaction,
                  child_run_id: childRunId
                }
              })
            });

            if (sendResponse.ok) {
              const respData = await sendResponse.json();
              let aiContent = respData.response || respData.message;

              // Extract user_message from JSON response if present
              if (aiContent) {
                try {
                  const parsed = typeof aiContent === 'string' ? JSON.parse(aiContent) : aiContent;
                  if (parsed.user_message) {
                    aiContent = parsed.user_message;
                  }
                } catch (e) {
                  // Not JSON, use as-is
                }
              }

              if (aiContent) {
                setMessages(prev => [...prev, {
                  id: `ai-${Date.now()}`,
                  type: 'assistant',
                  content: aiContent,
                  timestamp: new Date().toISOString()
                }]);
              }
            }
          } catch (aiErr) {
            console.error('Failed to get AI response for invoices/bills:', aiErr);
          } finally {
            setIsLoading(false);
          }
        } else {
          console.warn('⚠️ [Entity Update] Could not create thread/assistant for AI trigger');
        }
      } else {
        throw new Error(data.error || 'Failed to update');
      }
    } catch (err) {
      setMessages(prev => [...prev, {
        id: Date.now(),
        type: 'error',
        content: `❌ Failed to update ${noun}: ${err.message}`,
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setIsUpdatingEntity(false);
      setSelectedEntityId('');
    }
  };

  // Handle reallocation
  const handleReallocation = async () => {
    if (!enableReallocation || !parentRunId) return;

    const allocations = Object.entries(allocationAmounts)
      .filter(([_, amt]) => amt && parseFloat(amt) > 0)
      .map(([docNum, amt]) => ({ invoice_id: docNum, amount: parseFloat(amt) }));

    if (allocations.length === 0) {
      alert('❌ No valid allocations were entered.');
      return;
    }

    const docType = transaction.agent_type === 'bill_agent' ? 'Bill' : 'Invoice';
    const totalAllocated = allocations.reduce((s, a) => s + a.amount, 0);
    const transactionAmount = Math.abs(parseFloat(transaction.Amount) || 0);

    // Final validation: ensure total allocation doesn't exceed transaction amount
    if (totalAllocated > transactionAmount) {
      alert(`❌ Total allocation (${formatAmount(totalAllocated)}) exceeds payment amount (${formatAmount(transactionAmount)}). Please adjust your allocations.`);
      return;
    }

    setIsUpdatingAllocation(true);

    let userMsg = `Please process the following reallocations:\n\n`;
    userMsg += `| ${docType} Number | Allocated Amount |\n`;
    userMsg += `|------------------|------------------|\n`;
    allocations.forEach(a => {
      userMsg += `| ${a.invoice_id} | ${formatAmount(a.amount)} |\n`;
    });
    userMsg += `\nTotal: ${formatAmount(totalAllocated)}`;

    setMessages(prev => [...prev, { id: Date.now(), type: 'user', content: userMsg, timestamp: new Date().toISOString() }]);

    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://platform.sankalvax.ai';
      const response = await fetch(`${API_BASE_URL}/api/bank/v2/workflows/update-allocation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          parent_run_id: parentRunId,
          transaction_index: transaction.transaction_index,
          allocations
        })
      });

      const data = await response.json();

      if (data.success) {
        // Update transaction state with manual allocations so UI reflects the change
        const updatedTransaction = {
          ...transaction,
          manual_allocations: allocations,
          allocation_modified: true
        };
        setTransaction(updatedTransaction);

        // Save transaction with allocations to database for persistence
        if (childRunId) {
          fetch(`/api/bank/v2/workflows/save-transaction/${childRunId}`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ transaction: updatedTransaction })
          }).catch(err => console.warn('Failed to save transaction with allocations:', err));
        }

        setMessages(prev => [...prev, {
          id: Date.now(),
          type: 'assistant',
          content: `✅ Allocations updated: ${allocations.length} ${docType.toLowerCase()}(s), total: ${formatAmount(totalAllocated)}`,
          timestamp: new Date().toISOString()
        }]);
        setEnableReallocation(false);
        setAllocationAmounts({});
      } else {
        throw new Error(data.error || 'Failed to update');
      }
    } catch (err) {
      setMessages(prev => [...prev, {
        id: Date.now(),
        type: 'error',
        content: `❌ Failed to reallocate: ${err.message}`,
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setIsUpdatingAllocation(false);
    }
  };

  // Handle Create Payment button click - simply sends "create payment" message through existing chat flow
  const handleCreatePayment = () => {
    if (isLoading) return;
    setInputMessage('create payment');
    // Use setTimeout to ensure state is updated before triggering send
    setTimeout(() => {
      document.getElementById('bank-chat-send-btn')?.click();
    }, 50);
  };

  // Handle Create Journal Entry button click - simply sends "create journal entry" message through existing chat flow
  const handleCreateJE = () => {
    if (isLoading) return;
    setInputMessage('create journal entry');
    // Use setTimeout to ensure state is updated before triggering send
    setTimeout(() => {
      document.getElementById('bank-chat-send-btn')?.click();
    }, 50);
  };

  // Handle JE line selection toggle
  const handleJeLineSelect = (lineIndex) => {
    setSelectedJeLines(prev => ({
      ...prev,
      [lineIndex]: !prev[lineIndex]
    }));
  };

  // Handle JE line account change
  const handleJeAccountChange = (lineIndex, accountId) => {
    setJeLineUpdates(prev => ({
      ...prev,
      [lineIndex]: accountId
    }));
  };

  // Handle apply JE updates (account, memo, department, class, location)
  const handleApplyJeUpdates = async () => {
    // Collect all line indices that have any changes
    const allLineIndices = new Set([
      ...Object.keys(jeLineUpdates),
      ...Object.keys(jeLineMemos),
      ...Object.keys(jeLineDepartments),
      ...Object.keys(jeLineClasses),
      ...Object.keys(jeLineLocations)
    ]);

    const updates = [];
    const updateSummaryParts = [];

    allLineIndices.forEach(lineIndex => {
      const idx = parseInt(lineIndex);
      const line = jeLines[idx] || {};

      const accountId = jeLineUpdates[idx];
      const memo = jeLineMemos[idx];
      const deptId = jeLineDepartments[idx];
      const classId = jeLineClasses[idx];
      const locId = jeLineLocations[idx];

      // Only include if there's at least one change
      if (!accountId && !memo && !deptId && !classId && !locId) return;

      // Use existing account if no new account selected
      const finalAccountId = accountId || line.nsaccountid || line.account_id;

      if (!finalAccountId) {
        // Skip lines without any account (either new or existing)
        return;
      }

      updates.push({
        line_index: idx,
        new_account_id: finalAccountId,
        memo: memo || '',
        department_id: deptId || '',
        class_id: classId || '',
        location_id: locId || ''
      });

      // Build summary
      const parts = [`Line ${idx + 1}:`];
      if (accountId) parts.push(`Account → ${accountId}`);
      if (memo) parts.push(`Memo → ${memo}`);
      if (deptId) parts.push(`Dept → ${deptId}`);
      if (classId) parts.push(`Class → ${classId}`);
      if (locId) parts.push(`Location → ${locId}`);
      updateSummaryParts.push(parts.join(' '));
    });

    if (updates.length === 0) {
      alert('No changes to apply. Please select account, memo, department, class, or location for at least one line.');
      return;
    }

    setIsApplyingJeUpdates(true);

    // Add user message showing update
    setMessages(prev => [...prev, {
      id: Date.now(),
      type: 'user',
      content: `Applying updates:\n${updateSummaryParts.join('\n')}`,
      timestamp: new Date().toISOString()
    }]);

    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://platform.sankalvax.ai';
      const response = await fetch(`${API_BASE_URL}/api/bank/v2/workflows/update-je-lines`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          parent_run_id: parentRunId,
          transaction_index: transaction.transaction_index,
          updates
        })
      });

      const data = await response.json();

      if (data.success) {
        // Update local state with all changes
        setJeLines(prev => prev.map((line, idx) => {
          const update = updates.find(u => u.line_index === idx);
          if (update) {
            // Find account name from groups
            let accountName = update.new_account_id;
            Object.values(accountGroups).forEach(accounts => {
              const found = accounts.find(a => a.id === update.new_account_id);
              if (found) accountName = found.name;
            });
            return {
              ...line,
              new_account_id: update.new_account_id,
              nsaccountid: update.new_account_id,
              nsaccountname: accountName,
              account_name: accountName,
              memo: update.memo !== undefined && update.memo !== '' ? update.memo : line.memo,
              department_id: update.department_id !== undefined && update.department_id !== '' ? update.department_id : line.department_id,
              class_id: update.class_id !== undefined && update.class_id !== '' ? update.class_id : line.class_id,
              location_id: update.location_id !== undefined && update.location_id !== '' ? update.location_id : line.location_id
            };
          }
          return line;
        }));

        setMessages(prev => [...prev, {
          id: Date.now(),
          type: 'assistant',
          content: `✅ Applied ${updates.length} update(s) successfully.`,
          timestamp: new Date().toISOString()
        }]);

        // Clear all pending updates
        setJeLineUpdates({});
        setJeLineMemos({});
        setJeLineDepartments({});
        setJeLineClasses({});
        setJeLineLocations({});
        setSelectedJeLines({});
      } else {
        throw new Error(data.error || 'Failed to apply updates');
      }
    } catch (err) {
      setMessages(prev => [...prev, {
        id: Date.now(),
        type: 'error',
        content: `❌ Failed to apply updates: ${err.message}`,
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setIsApplyingJeUpdates(false);
    }
  };

  // Handle mass update for selected JE lines
  const handleJeMassUpdate = () => {
    if (!jeMassUpdateAccountId) {
      alert('Please select an account for mass update');
      return;
    }

    const selectedIndices = Object.entries(selectedJeLines)
      .filter(([_, selected]) => selected)
      .map(([idx, _]) => parseInt(idx));

    if (selectedIndices.length === 0) {
      alert('Please select at least one line to update');
      return;
    }

    // Apply mass update to all selected lines
    const newUpdates = {};
    selectedIndices.forEach(idx => {
      newUpdates[idx] = jeMassUpdateAccountId;
    });

    setJeLineUpdates(prev => ({ ...prev, ...newUpdates }));
    setJeMassUpdateEnabled(false);
    setJeMassUpdateAccountId('');
  };

  // Get pending JE updates for preview (account, memo, department, class, location)
  const getPendingJeUpdates = () => {
    const updates = [];

    // Collect all line indices that have any changes
    const allLineIndices = new Set([
      ...Object.keys(jeLineUpdates),
      ...Object.keys(jeLineMemos),
      ...Object.keys(jeLineDepartments),
      ...Object.keys(jeLineClasses),
      ...Object.keys(jeLineLocations)
    ]);

    allLineIndices.forEach(lineIndex => {
      const idx = parseInt(lineIndex);
      const line = jeLines[idx] || {};

      // Skip if JE already created for THIS specific line
      if (line.viewje || line.je_id || line.ns_je_id) {
        return;
      }

      const accountId = jeLineUpdates[idx];
      const memo = jeLineMemos[idx];
      const deptId = jeLineDepartments[idx];
      const classId = jeLineClasses[idx];
      const locId = jeLineLocations[idx];

      // Only include if there's at least one change
      if (!accountId && !memo && !deptId && !classId && !locId) return;

      // Find account name from groups
      let accountName = accountId || '';
      if (accountId) {
        Object.values(accountGroups).forEach(accounts => {
          const found = accounts.find(a => a.id === accountId);
          if (found) accountName = found.name;
        });
      }

      // Find department, class, location names
      const deptName = deptId ? (departmentList.find(d => d.id === deptId)?.name || deptId) : '';
      const className = classId ? (classList.find(c => c.id === classId)?.name || classId) : '';
      const locName = locId ? (locationList.find(l => l.id === locId)?.name || locId) : '';

      updates.push({
        lineIndex: idx,
        description: line.description || line.memo || `Line ${idx + 1}`,
        amount: formatAmount(line.amount || line.debit || line.credit || 0),
        newAccountId: accountId || '',
        newAccountName: accountName,
        memo: memo || '',
        deptId: deptId || '',
        deptName: deptName,
        classId: classId || '',
        className: className,
        locId: locId || '',
        locName: locName
      });
    });

    return updates.sort((a, b) => a.lineIndex - b.lineIndex);
  };

  // Handle transfer to JE
  const handleTransferToJE = async () => {
    if (!parentRunId) return;

    const confirmMsg = `Transfer this transaction (${formatAmount(transaction.Amount)} - ${getPredictedName()}) to JE Agent?`;
    if (!window.confirm(confirmMsg)) return;

    setIsTransferringToJE(true);

    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://platform.sankalvax.ai';
      const response = await fetch(`${API_BASE_URL}/api/bank/v2/workflows/transfer-to-je`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          parent_run_id: parentRunId,
          transaction_index: transaction.transaction_index
        })
      });

      const data = await response.json();

      if (data.success) {
        // Update local transaction state
        const updatedTransaction = {
          ...transaction,
          agent_type: 'je_agent',
          transferred_to_je: true,
          original_agent_type: data.original_type || transaction.agent_type
        };
        setTransaction(updatedTransaction);

        // Save updated transaction to child workflow for persistence
        if (childRunId) {
          fetch(`${API_BASE_URL}/api/bank/v2/workflows/save-transaction/${childRunId}`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ transaction: updatedTransaction })
          }).catch(err => console.warn('Failed to save JE transfer:', err));
        }

        setMessages(prev => [...prev, {
          id: Date.now(),
          type: 'assistant',
          content: `✅ Successfully transferred transaction to JE Agent! The right panel will now show Journal Entry options.`,
          timestamp: new Date().toISOString()
        }]);
      } else {
        throw new Error(data.error || 'Failed to transfer');
      }
    } catch (err) {
      setMessages(prev => [...prev, {
        id: Date.now(),
        type: 'error',
        content: `❌ Transfer failed: ${err.message}`,
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setIsTransferringToJE(false);
    }
  };

  const handleBack = () => {
    // Navigate back to BankChatHistory with openRun param to auto-open TransactionsModal
    if (parentRunId) {
      navigate(`/bank-chat-history?openRun=${parentRunId}`);
    } else {
      navigate(-1);
    }
  };

  const handleDebug = () => {
    const params = new URLSearchParams();
    if (parentRunId) params.set('parentRun', parentRunId);
    if (childRunId) params.set('childRun', childRunId);
    navigate(`/bank-debug?${params.toString()}`);
  };

  const getBadgeColor = () => {
    switch (transaction?.agent_type) {
      case 'bank_agent': return 'bg-emerald-500';
      case 'bill_agent': return 'bg-amber-500';
      default: return 'bg-indigo-500';
    }
  };

  // Check if any allocation is valid
  const isAnyAllocationValid = enableReallocation &&
    Object.values(allocationAmounts).some(v => v && parseFloat(v) > 0);

  if (error) {
    return (
      <MainLayout title="Bank Agent Chat">
        <div className="flex items-center justify-center h-[calc(100vh-8rem)]">
          <div className="text-center p-10 bg-white dark:bg-gray-800 rounded-xl shadow-lg">
            <h2 className="text-red-600 dark:text-red-400 text-xl font-semibold mb-3">Error</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">{error}</p>
            <button onClick={handleBack} className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-lg font-medium hover:shadow-lg transition-all">
              <ArrowLeft className="w-4 h-4" />
              Go Back
            </button>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (!transaction) {
    return (
      <MainLayout title="Bank Agent Chat">
        <div className="flex flex-col items-center justify-center h-[calc(100vh-8rem)] gap-4">
          <div className="w-10 h-10 border-4 border-gray-200 border-t-indigo-500 rounded-full animate-spin"></div>
          <p className="text-gray-600 dark:text-gray-400 text-sm">Loading transaction...</p>
        </div>
      </MainLayout>
    );
  }

  const openDocs = getOpenDocs();
  const suggestedAlloc = getSuggestedAllocation();
  const paymentLinks = getPaymentLinks();
  const similarEntities = getSimilarEntities();
  const entityNoun = transaction.agent_type === 'bill_agent' ? 'Vendor' : 'Customer';
  const docNoun = transaction.agent_type === 'bill_agent' ? 'Bills' : 'Invoices';

  return (
    <MainLayout title="Bank Agent Chat">
      <div className="flex flex-col h-[calc(100vh-8rem)] bg-white dark:bg-gray-900 rounded-lg overflow-hidden">
        {/* Header */}
        <div className="flex items-center gap-4 px-6 py-4 bg-gradient-to-r from-indigo-500 to-purple-600 text-white">
          <button onClick={handleBack} className="p-2 bg-white/20 rounded-lg hover:bg-white/30 transition-all">
            <ArrowLeft className="w-5 h-5" />
          </button>

          <div className="flex items-center gap-3 flex-1">
            <div className="relative p-2.5 bg-white/20 rounded-xl">
              <Bot className="w-7 h-7 text-white" />
              <div className="absolute -bottom-1 -right-1 p-0.5 bg-gradient-to-r from-yellow-400 to-amber-500 rounded-full">
                <Sparkles className="w-2.5 h-2.5 text-white" />
              </div>
            </div>
            <div>
              <h1 className="text-lg font-semibold">{getTransactionTypeName(transaction.agent_type)} Approval</h1>
              <p className="text-xs opacity-90">Transaction #{transaction.transaction_index + 1}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {isAdmin && (parentRunId || childRunId) && (
              <button onClick={handleDebug} className="p-2 bg-white/20 rounded-lg hover:bg-white/30 transition-all" title="View Execution Logs">
                <Bug className="w-5 h-5" />
              </button>
            )}
            <div className={`px-3 py-1.5 ${getBadgeColor()} rounded-full text-xs font-semibold`}>
              {transaction.agent_type === 'bank_agent' ? 'AR' : transaction.agent_type === 'bill_agent' ? 'AP' : 'JE'}
            </div>
          </div>
        </div>

        {/* Main Content - Two Panel Layout */}
        <div className="flex flex-1 overflow-hidden">
          {/* LEFT: Chat Panel */}
          <div className="flex-1 flex flex-col border-r border-gray-200 dark:border-gray-700">
            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 bg-gray-50 dark:bg-gray-800 flex flex-col gap-3">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex gap-2 max-w-[85%] ${
                    message.type === 'user' ? 'self-end flex-row-reverse' :
                    message.type === 'error' ? 'self-center' : 'self-start'
                  }`}
                >
                  {message.type === 'assistant' && (
                    <div className="w-8 h-8 rounded-full bg-gradient-to-r from-indigo-500 to-purple-600 flex items-center justify-center text-white flex-shrink-0">
                      <Bot className="w-4 h-4" />
                    </div>
                  )}
                  <div className={`px-3 py-2 rounded-lg text-sm leading-relaxed whitespace-pre-wrap ${
                    message.type === 'assistant' ? 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-600' :
                    message.type === 'user' ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white' :
                    'bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-200 border border-red-200 dark:border-red-700'
                  }`}>
                    {formatMessageContent(message.content)}
                  </div>
                </div>
              ))}

              {isLoading && (
                <div className="flex gap-2 self-start">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-r from-indigo-500 to-purple-600 flex items-center justify-center text-white">
                    <Bot className="w-4 h-4" />
                  </div>
                  <div className="flex items-center gap-1 px-3 py-2 bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                    <span className="w-2 h-2 bg-indigo-500 rounded-full animate-bounce" style={{ animationDelay: '0s' }}></span>
                    <span className="w-2 h-2 bg-indigo-500 rounded-full animate-bounce" style={{ animationDelay: '0.15s' }}></span>
                    <span className="w-2 h-2 bg-indigo-500 rounded-full animate-bounce" style={{ animationDelay: '0.3s' }}></span>
                  </div>
                </div>
              )}
              <div ref={bottomRef} />
            </div>

            {/* Input */}
            <div className="flex gap-2 p-4 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
              <input
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && !isLoading && handleSendMessage()}
                placeholder="Type your message..."
                disabled={isLoading}
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm outline-none focus:border-indigo-500 disabled:bg-gray-50 dark:disabled:bg-gray-800 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              />
              <button
                id="bank-chat-send-btn"
                onClick={handleSendMessage}
                disabled={!inputMessage.trim() || isLoading}
                className="px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-lg disabled:opacity-50"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* RIGHT: Transaction Status Panel */}
          <div className="w-[450px] flex-shrink-0 overflow-y-auto bg-white dark:bg-gray-900 p-4">
            {/* ======================== JE AGENT PANEL ======================== */}
            {transaction.agent_type === 'je_agent' ? (
              <>
                <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200 mb-4 flex items-center gap-2">
                  📋 Journal Entry Lines
                </h3>

                {/* Loading Accounts */}
                {isLoadingAccounts && (
                  <div className="flex items-center gap-2 text-sm text-gray-500 py-4">
                    <div className="w-4 h-4 border-2 border-gray-300 border-t-indigo-500 rounded-full animate-spin"></div>
                    Loading accounts...
                  </div>
                )}

                {/* JE Lines Table */}
                {!isLoadingAccounts && (
                  <div className="space-y-4">
                    {/* Transaction Details Summary */}
                    <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 text-sm space-y-1">
                      <div><span className="font-medium">Amount:</span> {formatAmount(transaction.Amount)}</div>
                      <div><span className="font-medium">Date:</span> {transaction.trandate}</div>
                      <div><span className="font-medium">Bank:</span> {transaction.bank || 'N/A'}</div>
                      {transaction.transferred_to_je && transaction.original_agent_type && (
                        <div className="text-amber-600 dark:text-amber-400">
                          <span className="font-medium">Transferred from:</span> {transaction.original_agent_type === 'bank_agent' ? 'AR (Customer Payment)' : 'AP (Vendor Payment)'}
                        </div>
                      )}
                      {(transaction.customername || transaction.vendorname) && (
                        <div>
                          <span className="font-medium">{transaction.customername ? 'Customer' : 'Vendor'}:</span> {transaction.customername || transaction.vendorname}
                        </div>
                      )}
                      <div><span className="font-medium">Description:</span> <span className="break-words">{transaction.description}</span></div>
                    </div>

                    {/* JE Lines Table - Responsive with horizontal scroll */}
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs min-w-[650px]">
                          <thead className="bg-gray-100 dark:bg-gray-800">
                            <tr>
                              <th className="px-2 py-2 text-left w-8">
                                <input
                                  type="checkbox"
                                  onChange={(e) => {
                                    const newSelected = {};
                                    jeLines.forEach((_, idx) => {
                                      newSelected[idx] = e.target.checked;
                                    });
                                    setSelectedJeLines(newSelected);
                                  }}
                                  checked={jeLines.length > 0 && Object.values(selectedJeLines).filter(Boolean).length === jeLines.length}
                                  className="w-3.5 h-3.5 rounded"
                                />
                              </th>
                              <th className="px-2 py-2 text-left w-10">#</th>
                              <th className="px-2 py-2 text-left min-w-[100px]">Description</th>
                              <th className="px-2 py-2 text-left w-12">Type</th>
                              <th className="px-2 py-2 text-right w-24">Amount</th>
                              <th className="px-2 py-2 text-left min-w-[120px]">Current Account</th>
                              <th className="px-2 py-2 text-left min-w-[140px]">New Account</th>
                              <th className="px-2 py-2 text-left min-w-[100px]">Memo</th>
                              <th className="px-2 py-2 text-left min-w-[100px]">Dept</th>
                              <th className="px-2 py-2 text-left min-w-[100px]">Class</th>
                              <th className="px-2 py-2 text-left min-w-[100px]">Location</th>
                              <th className="px-2 py-2 text-left w-16">JE</th>
                            </tr>
                          </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                          {jeLines.length === 0 ? (
                            <tr>
                              <td colSpan="12" className="px-4 py-6 text-center text-gray-500">
                                No journal entry lines available
                              </td>
                            </tr>
                          ) : (
                            jeLines.map((line, idx) => {
                              // Check if JE already created - check both transaction level and line level
                              // transaction.ns_JE_id/ns_je_id = transaction level, line.viewje/je_id/ns_je_id = line level
                              const jeCreated = !!(transaction.ns_JE_id?.[0] || transaction.ns_je_id?.[0] || line.viewje || line.je_id || line.ns_je_id);

                              // Check if there's a pending update for this line
                              const pendingAccountId = jeLineUpdates[idx];
                              let displayLabel = line.nsaccountname || line.account_name || line.current_account || '';
                              let displayId = line.nsaccountid || '';

                              // If there's a pending selection, show that instead
                              if (pendingAccountId) {
                                displayId = pendingAccountId;
                                // Find the account name from groups
                                Object.values(accountGroups).forEach(accounts => {
                                  const found = accounts.find(a => a.id === pendingAccountId);
                                  if (found) displayLabel = found.name;
                                });
                              }

                              const currentLabel = displayLabel;
                              const currentId = displayId;
                              return (
                                <tr key={idx} className={`${selectedJeLines[idx] ? 'bg-indigo-50 dark:bg-indigo-900/20' : ''} ${jeCreated ? 'bg-green-50 dark:bg-green-900/20' : ''} hover:bg-gray-50 dark:hover:bg-gray-800`}>
                                  <td className="px-2 py-2">
                                    <input
                                      type="checkbox"
                                      checked={!!selectedJeLines[idx]}
                                      onChange={() => handleJeLineSelect(idx)}
                                      disabled={jeCreated}
                                      className="w-3.5 h-3.5 rounded"
                                    />
                                  </td>
                                  <td className="px-2 py-2 text-gray-600 dark:text-gray-400">{idx + 1}</td>
                                  <td className="px-2 py-2 text-gray-700 dark:text-gray-300 truncate max-w-[100px]" title={line.description || line.memo}>
                                    {line.description || line.memo || '-'}
                                  </td>
                                  <td className="px-2 py-2">
                                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                      line.type === 'debit' || line.credit === 0
                                        ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                                        : 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                                    }`}>
                                      {line.type === 'debit' || line.credit === 0 ? 'DR' : 'CR'}
                                    </span>
                                  </td>
                                  <td className="px-2 py-2 text-right font-mono text-gray-700 dark:text-gray-300">
                                    {formatAmount(line.amount || line.debit || line.credit)}
                                  </td>
                                  <td className="px-2 py-2 text-gray-600 dark:text-gray-400 truncate max-w-[100px]" title={currentLabel + (currentId ? ` (${currentId})` : '')}>
                                    {currentLabel}{currentId ? ` (${currentId})` : ''}
                                  </td>
                                  <td className="px-2 py-2">
                                    <select
                                      value={jeLineUpdates[idx] || ''}
                                      onChange={(e) => handleJeAccountChange(idx, e.target.value)}
                                      disabled={jeCreated}
                                      className={`w-full px-1.5 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 ${jeCreated ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    >
                                      <option value="">{currentLabel ? `-- Keep (${currentLabel}) --` : '-- Select --'}</option>
                                      {Object.entries(accountGroups).map(([groupName, accounts]) => (
                                        <optgroup key={groupName} label={groupName}>
                                          {accounts.map(acct => (
                                            <option key={acct.id} value={acct.id}>
                                              {acct.name}
                                            </option>
                                          ))}
                                        </optgroup>
                                      ))}
                                    </select>
                                  </td>
                                  <td className="px-2 py-2">
                                    <input
                                      type="text"
                                      value={jeLineMemos[idx] !== undefined ? jeLineMemos[idx] : (line.memo || '')}
                                      onChange={(e) => setJeLineMemos(prev => ({ ...prev, [idx]: e.target.value }))}
                                      placeholder="Memo..."
                                      disabled={jeCreated}
                                      className={`w-full px-1.5 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 ${jeCreated ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    />
                                  </td>
                                  <td className="px-2 py-2">
                                    <select
                                      value={jeLineDepartments[idx] || line.department_id || ''}
                                      onChange={(e) => setJeLineDepartments(prev => ({ ...prev, [idx]: e.target.value }))}
                                      disabled={jeCreated}
                                      className={`w-full px-1.5 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 ${jeCreated ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    >
                                      <option value="">-- Select Dept --</option>
                                      {departmentList.map(dept => (
                                        <option key={dept.id} value={dept.id}>{dept.name}</option>
                                      ))}
                                    </select>
                                  </td>
                                  <td className="px-2 py-2">
                                    <select
                                      value={jeLineClasses[idx] || line.class_id || ''}
                                      onChange={(e) => setJeLineClasses(prev => ({ ...prev, [idx]: e.target.value }))}
                                      disabled={jeCreated}
                                      className={`w-full px-1.5 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 ${jeCreated ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    >
                                      <option value="">-- Select Class --</option>
                                      {classList.map(cls => (
                                        <option key={cls.id} value={cls.id}>{cls.name}</option>
                                      ))}
                                    </select>
                                  </td>
                                  <td className="px-2 py-2">
                                    <select
                                      value={jeLineLocations[idx] || line.location_id || ''}
                                      onChange={(e) => setJeLineLocations(prev => ({ ...prev, [idx]: e.target.value }))}
                                      disabled={jeCreated}
                                      className={`w-full px-1.5 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 ${jeCreated ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    >
                                      <option value="">-- Select Location --</option>
                                      {locationList.map(loc => (
                                        <option key={loc.id} value={loc.id}>{loc.name}</option>
                                      ))}
                                    </select>
                                  </td>
                                  <td className="px-2 py-2">
                                    {/* Show created JE link from transaction level (ns_je_id/ns_JE_id) or line level (viewje) */}
                                    {(transaction.ns_JE_id?.[0] || transaction.ns_je_id?.[0] || line.viewje) ? (
                                      <a
                                        href={getNetSuiteUrl('journal', transaction.ns_JE_id?.[0] || transaction.ns_je_id?.[0] || line.viewje)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-indigo-600 hover:text-indigo-800 hover:underline text-xs"
                                      >
                                        {transaction.ns_JE_doc?.[0] || transaction.ns_je_doc?.[0] || 'View JE'}
                                      </a>
                                    ) : (
                                      <span className="text-gray-400">-</span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Pending Updates Preview */}
                    {getPendingJeUpdates().length > 0 && (
                      <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-3">
                        <div className="text-sm font-semibold text-yellow-800 dark:text-yellow-300 mb-2">
                          📝 Pending Updates ({getPendingJeUpdates().length} line(s))
                        </div>
                        <div className="space-y-2">
                          {getPendingJeUpdates().map((update, idx) => (
                            <div key={idx} className="text-xs text-yellow-700 dark:text-yellow-400 bg-yellow-100 dark:bg-yellow-900/30 rounded p-2">
                              <div className="font-semibold mb-1">Line {update.lineIndex + 1} - {update.amount}</div>
                              <div className="grid grid-cols-2 gap-1 text-[10px]">
                                {update.newAccountName && (
                                  <div><span className="text-yellow-600">Account:</span> <span className="text-green-600 dark:text-green-400">{update.newAccountName}</span></div>
                                )}
                                {update.memo && (
                                  <div><span className="text-yellow-600">Memo:</span> <span className="text-green-600 dark:text-green-400">{update.memo}</span></div>
                                )}
                                {update.deptName && (
                                  <div><span className="text-yellow-600">Dept:</span> <span className="text-green-600 dark:text-green-400">{update.deptName}</span></div>
                                )}
                                {update.className && (
                                  <div><span className="text-yellow-600">Class:</span> <span className="text-green-600 dark:text-green-400">{update.className}</span></div>
                                )}
                                {update.locName && (
                                  <div><span className="text-yellow-600">Location:</span> <span className="text-green-600 dark:text-green-400">{update.locName}</span></div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Mass Update Section */}
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                      <div className="flex items-center gap-3 mb-2">
                        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                          <input
                            type="checkbox"
                            checked={jeMassUpdateEnabled}
                            onChange={(e) => setJeMassUpdateEnabled(e.target.checked)}
                            className="w-4 h-4 rounded border-gray-300"
                          />
                          <span className="font-medium">Mass Update</span>
                        </label>
                        {jeMassUpdateEnabled && (
                          <span className="text-xs text-gray-500">
                            ({Object.values(selectedJeLines).filter(Boolean).length} line(s) selected)
                          </span>
                        )}
                      </div>

                      {jeMassUpdateEnabled && (
                        <div className="space-y-2">
                          {/* Account Search */}
                          <input
                            type="text"
                            placeholder="Search accounts..."
                            value={accountSearchTerm}
                            onChange={(e) => setAccountSearchTerm(e.target.value)}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
                          />

                          <select
                            value={jeMassUpdateAccountId}
                            onChange={(e) => setJeMassUpdateAccountId(e.target.value)}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
                            size={6}
                          >
                            <option value="">-- Select Account --</option>
                            {Object.entries(accountGroups).map(([groupName, accounts]) => (
                              <optgroup key={groupName} label={groupName}>
                                {accounts
                                  .filter(acct => !accountSearchTerm || acct.name.toLowerCase().includes(accountSearchTerm.toLowerCase()))
                                  .map(acct => (
                                    <option key={acct.id} value={acct.id}>
                                      {acct.name}
                                    </option>
                                  ))}
                              </optgroup>
                            ))}
                          </select>

                          <button
                            onClick={handleJeMassUpdate}
                            disabled={!jeMassUpdateAccountId || Object.values(selectedJeLines).filter(Boolean).length === 0}
                            className="w-full px-3 py-2 bg-amber-500 text-white text-sm font-medium rounded hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Apply to Selected
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Apply Updates Button */}
                    <button
                      onClick={handleApplyJeUpdates}
                      disabled={getPendingJeUpdates().length === 0 || isApplyingJeUpdates || isLoading}
                      className="w-full px-4 py-2.5 bg-indigo-500 text-white text-sm font-medium rounded-lg hover:bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isApplyingJeUpdates ? 'Applying Updates...' : `Apply ${getPendingJeUpdates().length} Update(s)`}
                    </button>

                    {/* Create JE Button - Only show when JE lines exist and no JE created yet */}
                    {paymentLinks.length === 0 && (
                      <button
                        onClick={handleCreateJE}
                        disabled={isLoading}
                        className="w-full mt-3 px-4 py-2.5 bg-green-500 text-white text-sm font-medium rounded-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {isLoading ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            Creating...
                          </>
                        ) : (
                          <>
                            ✨ Create Journal Entry
                          </>
                        )}
                      </button>
                    )}
                  </div>
                )}
              </>
            ) : (
              /* ======================== AR/AP AGENT PANEL ======================== */
              <>
                <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200 mb-4 flex items-center gap-2">
                  📋 Transaction Status
                </h3>

                <div className="space-y-4">
                  {/* Enable Reallocation Checkbox - Hide when payment already created */}
                  {openDocs.length > 0 && paymentLinks.length === 0 && (
                    <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                      <input
                        type="checkbox"
                        checked={enableReallocation}
                        onChange={(e) => {
                          setEnableReallocation(e.target.checked);
                          if (!e.target.checked) setAllocationAmounts({});
                        }}
                        className="w-4 h-4 rounded border-gray-300"
                      />
                      Enable Reallocation
                    </label>
                  )}

                  {/* Transaction Details */}
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="font-semibold text-gray-700 dark:text-gray-300">Predicted {entityNoun}:</span>{' '}
                      <span className="text-gray-600 dark:text-gray-400">{getPredictedName()}</span>
                    </div>
                    <div>
                      <span className="font-semibold text-gray-700 dark:text-gray-300">Matched in NetSuite:</span>{' '}
                      {isMatched() ? (
                        <span className="text-green-600">✅ {getMatchedName()}</span>
                      ) : (
                        <span className="text-red-600">❌ Not matched</span>
                      )}
                    </div>
                    <div>
                      <span className="font-semibold text-gray-700 dark:text-gray-300">Amount:</span>{' '}
                      <span className="text-gray-600 dark:text-gray-400">{formatAmount(transaction.Amount)}</span>
                    </div>
                    <div>
                      <span className="font-semibold text-gray-700 dark:text-gray-300">Date:</span>{' '}
                      <span className="text-gray-600 dark:text-gray-400">{transaction.trandate}</span>
                    </div>
                    <div>
                      <span className="font-semibold text-gray-700 dark:text-gray-300">Bank:</span>{' '}
                      <span className="text-gray-600 dark:text-gray-400">{transaction.bank}</span>
                    </div>
                    <div>
                      <span className="font-semibold text-gray-700 dark:text-gray-300">Description:</span>{' '}
                      <span className="text-gray-600 dark:text-gray-400 break-words">{transaction.description}</span>
                    </div>
                  </div>

                  {/* Entity Selection (when NOT matched) */}
                  {!isMatched() && (
                    <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
                      <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        Select Correct {entityNoun}:
                      </div>

                      {/* Loading state */}
                      {isLoadingEntities && (
                        <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
                          <div className="w-4 h-4 border-2 border-gray-300 border-t-indigo-500 rounded-full animate-spin"></div>
                          Loading {entityNoun.toLowerCase()}s...
                        </div>
                      )}

                      {/* Dropdown with Search - use similarEntities if available, otherwise allEntities */}
                      {!isLoadingEntities && (
                        <>
                          {/* Search Input */}
                          <input
                            type="text"
                            placeholder={`Search ${entityNoun.toLowerCase()}s...`}
                            value={entitySearchTerm}
                            onChange={(e) => setEntitySearchTerm(e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 mb-2"
                          />

                          <select
                            value={selectedEntityId}
                            onChange={(e) => setSelectedEntityId(e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
                            size={Math.min(8, (similarEntities.length > 0 ? similarEntities : allEntities).filter(opt =>
                              !entitySearchTerm || opt.label?.toLowerCase().includes(entitySearchTerm.toLowerCase())
                            ).length + 1)}
                          >
                            <option value="">-- Select {entityNoun} --</option>
                            {/* Filter and show entities based on search term */}
                            {(similarEntities.length > 0 ? similarEntities : allEntities)
                              .filter(opt => !entitySearchTerm || opt.label?.toLowerCase().includes(entitySearchTerm.toLowerCase()))
                              .map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                          </select>

                          {/* Show count info */}
                          {similarEntities.length === 0 && allEntities.length > 0 && (
                            <div className="text-xs text-gray-400 mt-1">
                              {entitySearchTerm
                                ? `Found ${allEntities.filter(e => e.label?.toLowerCase().includes(entitySearchTerm.toLowerCase())).length} of ${allEntities.length}`
                                : `Showing all ${allEntities.length} ${entityNoun.toLowerCase()}s`}
                            </div>
                          )}
                          {similarEntities.length > 0 && (
                            <div className="text-xs text-gray-400 mt-1">
                              {similarEntities.length} similar {entityNoun.toLowerCase()}s found
                            </div>
                          )}

                          <button
                            onClick={handleEntityUpdate}
                            disabled={!selectedEntityId || isUpdatingEntity || isLoading}
                            className="mt-2 w-full px-4 py-2 bg-indigo-500 text-white text-sm font-medium rounded-lg hover:bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {isUpdatingEntity ? 'Updating...' : `Update ${entityNoun}`}
                          </button>

                          {/* Transfer to JE Button - Hide if transaction already has payment */}
                          {paymentLinks.length === 0 && (
                            <button
                              onClick={handleTransferToJE}
                              disabled={isTransferringToJE || isLoading}
                              className="mt-2 w-full px-4 py-2 bg-green-500 text-white text-sm font-medium rounded-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {isTransferringToJE ? 'Transferring...' : 'Transfer to JE Agent'}
                            </button>
                          )}

                        </>
                      )}
                    </div>
                  )}

                  {/* Open Invoices/Bills */}
                  {openDocs.length > 0 && (
                    <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
                      <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        🧾 Open {docNoun}:
                      </div>
                      <div className="space-y-2">
                        {openDocs.map((doc, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-sm">
                            <div className="flex-1 text-gray-600 dark:text-gray-400">
                              {doc.number} — {formatAmount(doc.amount)} {doc.duedate && `(Due: ${doc.duedate})`}
                            </div>
                            {enableReallocation && paymentLinks.length === 0 && (
                              <input
                                type="number"
                                min="0"
                                placeholder="0"
                                value={allocationAmounts[doc.number] || ''}
                                onChange={(e) => {
                                  let value = parseFloat(e.target.value) || 0;
                                  const maxDocAmt = Math.abs(parseFloat(doc.amount) || 0);
                                  const transactionAmount = Math.abs(parseFloat(transaction.Amount) || 0);

                                  // Check if value exceeds the document amount
                                  if (value > maxDocAmt) {
                                    alert(`Cannot allocate more than ${formatAmount(maxDocAmt)} for ${doc.number}`);
                                    value = maxDocAmt;
                                  }

                                  // Check if total allocation exceeds transaction amount
                                  let totalOthers = 0;
                                  Object.entries(allocationAmounts).forEach(([docNum, amt]) => {
                                    if (docNum !== doc.number && amt) {
                                      totalOthers += Math.abs(parseFloat(amt) || 0);
                                    }
                                  });
                                  const allowed = Math.max(transactionAmount - totalOthers, 0);
                                  if (value > allowed) {
                                    alert(`Total allocation exceeds payment amount ${formatAmount(transactionAmount)}. Adjusted ${doc.number} to ${formatAmount(allowed)}`);
                                    value = allowed;
                                  }

                                  setAllocationAmounts(prev => ({
                                    ...prev,
                                    [doc.number]: value || ''
                                  }));
                                }}
                                className="w-20 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Suggested/Current Allocation */}
                  {suggestedAlloc.length > 0 && (
                    <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
                      <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                        {transaction.manual_allocations?.length > 0 ? (
                          <>
                            <span className="text-green-600">✏️ Current Allocation (Modified):</span>
                          </>
                        ) : (
                          'Suggested Allocation:'
                        )}
                      </div>
                      {suggestedAlloc.map((s, idx) => (
                        <div key={idx} className={`text-sm ${s.isManual ? 'text-green-600 font-medium' : 'text-gray-600 dark:text-gray-400'}`}>
                          {s.number} — {formatAmount(s.amount)}
                        </div>
                      ))}

                      {/* Create Payment Button - Only show when allocation exists and no payment created yet */}
                      {paymentLinks.length === 0 && isMatched() && (
                        <div className="mt-3">
                          <button
                            onClick={handleCreatePayment}
                            disabled={isLoading}
                            className="w-full px-4 py-2.5 bg-green-500 text-white text-sm font-medium rounded-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                          >
                            {isLoading ? (
                              <>
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                Creating...
                              </>
                            ) : (
                              <>
                                ✨ Create {transaction.agent_type === 'bill_agent' ? 'Vendor Payment' : 'Customer Payment'}
                              </>
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Payment Links */}
                  {paymentLinks.length > 0 && (
                    <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
                      <div className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        ✅ {transaction.agent_type === 'bill_agent' ? 'Bill Payment' : transaction.agent_type === 'je_agent' ? 'Journal Entry' : 'Customer Payment'}:
                      </div>
                      {paymentLinks.map((link) => (
                        <div key={link.id}>
                          <a
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-indigo-600 hover:text-indigo-800 hover:underline"
                          >
                            View {link.label}
                          </a>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Reallocate Button - Hide when payment already created */}
                  {openDocs.length > 0 && paymentLinks.length === 0 && (
                    <div className="pt-3">
                      <button
                        onClick={handleReallocation}
                        disabled={!isAnyAllocationValid || isUpdatingAllocation || isLoading}
                        className="w-full px-4 py-2.5 bg-indigo-500 text-white text-sm font-medium rounded-lg hover:bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isUpdatingAllocation ? 'Processing...' : `Reallocate ${docNoun}`}
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default BankAgentChat;
